import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { BookButton, WhatsappButton } from './CtaButtons';
import DevoteeBadge from './DevoteeBadge';
import { SAI_BABA_IMG } from '../../lib/constants';

const HERO_IMG = SAI_BABA_IMG;

const maskParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};
const maskChild = {
  hidden: { y: '110%' },
  show: { y: '0%', transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const MaskLine = ({ children, className = '' }) => (
  <span className="block overflow-hidden">
    <motion.span variants={maskChild} className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-ivory pt-10 pb-4 md:pt-14 md:pb-8"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-32 right-0 h-[520px] w-[520px] rounded-full bg-gold/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/4 h-72 w-72 rounded-full bg-maroon/5 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 md:px-8 lg:grid-cols-12">
        {/* Left — deity image in temple arch */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-5"
        >
          <motion.div style={{ y: imgY }} className="relative mx-auto max-w-md">
            {/* spotlight behind */}
            <div className="absolute inset-0 -z-10 translate-y-6 scale-110 rounded-[999px_999px_24px_24px] bg-[radial-gradient(circle_at_50%_30%,rgba(212,175,55,0.35),transparent_65%)] blur-2xl" />
            <div className="overflow-hidden rounded-[999px_999px_18px_18px] border-2 border-gold/40 shadow-[0_30px_80px_-30px_rgba(92,22,22,0.6)]">
              <img
                src={HERO_IMG}
                alt="Sai Baba idol adorned for Mahotsava"
                className="h-[420px] w-full object-cover md:h-[520px]"
                loading="eager"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-maroon/30 via-transparent to-transparent" />
            </div>
          </motion.div>
        </motion.div>

        {/* Right — headline + copy */}
        <motion.div
          variants={maskParent}
          initial="hidden"
          animate="show"
          className="lg:col-span-7"
        >
          <MaskLine className="font-sans text-[11px] uppercase tracking-[0.35em] text-gold-deep">
            Omvedha Presents
          </MaskLine>

          <h1 className="mt-4 font-serif leading-[1.02]">
            <MaskLine className="text-5xl text-ink md:text-6xl lg:text-7xl">Sai Baba</MaskLine>
            <MaskLine className="gold-shimmer text-5xl italic md:text-6xl lg:text-7xl">
              Mahotsava
            </MaskLine>
          </h1>

          <motion.div variants={maskChild} className="mt-6 max-w-xl">
            <p className="font-serif text-lg text-gold-deep md:text-xl">
              Surrender in Faith. Receive His Blessings.
            </p>
            <p className="mt-4 font-sans text-base leading-relaxed text-ink/70 md:text-lg">
              Join the divine celebrations of Sai Baba Mahotsava 2026 in RajaRajeshwari Nagar,
              Bangalore. Through sacred rituals and heartfelt devotion, invite peace, prosperity and
              protection into your life.
            </p>
          </motion.div>

          <motion.div
            variants={maskChild}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <BookButton label="hero" />
            <WhatsappButton label="hero" />
          </motion.div>

          <motion.div variants={maskChild} className="mt-6">
            <DevoteeBadge />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
